/**
 * Preferencias de correo de cada persona (CDT-219, ADR-0019 y plan de correos).
 *
 * Hoy solo los avisos: cuatro interruptores, todos encendidos por defecto. Cada persona (cualquier rol, incluida
 * LECTURA) cambia los suyos; solo un ADMIN cambia los de otra persona. Sin fila guardada rigen
 * `PREFERENCIAS_POR_DEFECTO`.
 *
 * Diseño pensado para crecer: el reporte periódico (PR 6) se suma como otra clave (`reporte`) junto a `avisos` en la
 * entrada y en la salida, sin tocar lo que ya existe.
 */
import { z } from 'zod';
import { fechaIsoSchema } from './comunes';

/** Tipos de aviso por correo que una persona puede apagar. */
export const TIPOS_AVISO = ['COMENTARIO', 'ASIGNADA', 'ESTADO', 'FECHAS'] as const;
export type TipoAviso = (typeof TIPOS_AVISO)[number];

export const ETIQUETA_TIPO_AVISO: Record<TipoAviso, string> = {
  COMENTARIO: 'Comentarios en mis tareas',
  ASIGNADA: 'Me asignan una tarea',
  ESTADO: 'Cambios de estado',
  FECHAS: 'Fechas límite',
};

export const EMOJI_TIPO_AVISO: Record<TipoAviso, string> = {
  COMENTARIO: '💬',
  ASIGNADA: '👤',
  ESTADO: '✅',
  FECHAS: '⏰',
};

/**
 * Un booleano por tipo de aviso (`true` = se envía) y `propios`: «Avisarme también de lo que hago yo». `.strict()`: un
 * tipo desconocido es un error, no se ignora en silencio. Es la forma de las respuestas (siempre los cinco); la entrada
 * del PUT es `avisosCorreoEntradaSchema`, con `propios` opcional.
 *
 * `propios` va en minúscula a propósito: no es un tipo de aviso más (no está en `TIPOS_AVISO` ni cuenta en «4 de 4»),
 * sino un modificador que se aplica a todos. Apagado por defecto: nunca se avisa de lo que hizo uno mismo, salvo que
 * la persona lo pida (útil para probar o para tener copia de lo que hace).
 */
export const avisosCorreoSchema = z
  .object({
    COMENTARIO: z.boolean(),
    ASIGNADA: z.boolean(),
    ESTADO: z.boolean(),
    FECHAS: z.boolean(),
    propios: z.boolean(),
  })
  .strict();
export type AvisosCorreo = z.infer<typeof avisosCorreoSchema>;

/**
 * Avisos que llegan en el PUT: los cuatro tipos obligatorios y `propios` **opcional** (sin `.default()`), para que un
 * cliente anterior a la opción (script, token) siga funcionando. El servicio usa `false` si no viene, así que un PUT sin
 * `propios` la apaga: el PUT sigue siendo completo. Las respuestas (`avisosCorreoSchema`) siempre lo traen.
 */
export const avisosCorreoEntradaSchema = z
  .object({
    COMENTARIO: z.boolean(),
    ASIGNADA: z.boolean(),
    ESTADO: z.boolean(),
    FECHAS: z.boolean(),
    propios: z.boolean().optional(),
  })
  .strict();
export type AvisosCorreoEntrada = z.infer<typeof avisosCorreoEntradaSchema>;

/** Completa la entrada del PUT: sin `propios`, apagado. */
export const avisosDesdeEntrada = (avisos: AvisosCorreoEntrada): AvisosCorreo => ({
  ...avisos,
  propios: avisos.propios ?? false,
});

/** Entrada de `PUT /api/cuenta/correos` y `PUT /api/usuarios/:id/correos`: reemplaza todo (PUT completo). */
export const guardarPreferenciasCorreoSchema = z
  .object({
    avisos: avisosCorreoEntradaSchema,
  })
  .strict();
export type GuardarPreferenciasCorreoEntrada = z.infer<typeof guardarPreferenciasCorreoSchema>;

/** Respuesta de `GET /api/usuarios/correos` (solo ADMIN): los avisos vigentes de cada persona, por id. */
export const avisosPorPersonaSchema = z.record(z.string(), avisosCorreoSchema);
export type AvisosPorPersona = z.infer<typeof avisosPorPersonaSchema>;

/** Respuesta de los `GET` y `PUT` de preferencias de correo. */
export const preferenciasCorreoSchema = z.object({
  avisos: avisosCorreoSchema,
  /** `true` si el último cambio lo hizo otra persona (un ADMIN desde Personas). */
  modificadoPorAdmin: z.boolean(),
  /** Último cambio; `null` si la persona nunca las guardó (rigen las de por defecto). */
  actualizadoEn: fechaIsoSchema.nullable(),
});
export type PreferenciasCorreo = z.infer<typeof preferenciasCorreoSchema>;

/** Etiqueta y descripción del interruptor `propios` (la web y los correos dicen lo mismo). */
export const ETIQUETA_AVISO_PROPIOS = 'Avisarme también de lo que hago yo';

/**
 * Los cuatro avisos encendidos y `propios` apagado: lo que rige para quien nunca guardó sus preferencias. Solo lectura en profundidad (`as const` para
 * el compilador y `Object.freeze` en ejecución): para modificarlas, usa la copia de `preferenciasCorreoPorDefecto()`.
 */
export const PREFERENCIAS_POR_DEFECTO = Object.freeze({
  avisos: Object.freeze({
    COMENTARIO: true,
    ASIGNADA: true,
    ESTADO: true,
    FECHAS: true,
    propios: false,
  } as const),
  modificadoPorAdmin: false,
  actualizadoEn: null,
} as const) satisfies PreferenciasCorreo;

/** Cuántos de los cuatro tipos de aviso están encendidos (para el resumen «3 de 4»; `propios` no cuenta). */
export const avisosEncendidos = (avisos: AvisosCorreo): number =>
  TIPOS_AVISO.filter((tipo) => avisos[tipo]).length;

/** Copia de las preferencias por defecto (para no compartir ni mutar el objeto congelado). */
export const preferenciasCorreoPorDefecto = (): PreferenciasCorreo => ({
  avisos: { ...PREFERENCIAS_POR_DEFECTO.avisos },
  modificadoPorAdmin: false,
  actualizadoEn: null,
});
